/**
 * URL guards for values that come from data (research findings, the
 * customer-typed company URL, LLM-written markdown, query params) before
 * they reach an `href` or a navigation.
 */

const SAFE_PROTOCOLS = new Set(['http:', 'https:'])

/** A throwaway origin to resolve relative paths against: any absolute URL
 * that resolves to a different origin escaped the app. */
const PATH_BASE = 'http://same-origin.invalid'

/**
 * `raw` (trimmed) when it parses as an absolute http(s) URL, else null — any
 * other scheme (javascript:, data:, vbscript:, file:, …) or a relative URL
 * is rejected. The browser resolves an href with the same URL parser, so
 * the scheme checked here is the scheme it will follow; the text itself is
 * returned unnormalized so displayed and linked values stay identical.
 */
export function safeHttpUrl(raw: string | null | undefined): string | null {
  if (!raw) return null
  try {
    const url = new URL(raw)
    return SAFE_PROTOCOLS.has(url.protocol) ? raw.trim() : null
  } catch {
    return null
  }
}

/**
 * The same-origin relative path in `raw` ("/companies/x?tab=y"), or null for
 * anything that could leave the app: absolute URLs, protocol-relative
 * `//evil`, `/\\evil`, and paths the parser would re-read as a host (browsers
 * strip tabs/newlines, so `/\t/evil` is `//evil`).
 */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) return null
  try {
    const url = new URL(raw, PATH_BASE)
    if (url.origin !== PATH_BASE) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}
